import { ElementCreator } from './common/ElementCreator';
import { ElementMatcher } from './common/ElementMatcher';
const parseNumber = (value) => Number.parseInt(value, 10);
const galleryOverlayHtml = `
<div class="gallery-overlay">
  <div class="gallery-content">
    <div class="ratio"></div>
  </div>
  <span class="gallery-nav gallery-btn gallery-btn-close"><span class="icon"></span></span>
  <span class="gallery-nav gallery-btn gallery-btn-left"><span class="icon"></span></span>
  <span class="gallery-nav gallery-btn gallery-btn-right"><span class="icon"></span></span>
  <div class="gallery-nav gallery-caption"></div>
</div>
`;
class Dimensions {
    constructor(width, height) {
        this.width = width;
        this.height = height;
        this.ratio = width / height;
    }
}
class OpenedGallery {
    constructor(win, overlay, images, activeImage = 0) {
        this.win = win;
        this.overlay = overlay;
        this.images = images;
        this.activeImage = activeImage;
        this.doc = this.win.document;
        this.caption = this.getByClass(overlay, 'gallery-caption');
        this.content = this.getByClass(overlay, 'gallery-content');
        this.imgRatio = this.getByClass(this.content, 'ratio');
        this.reset();
        this.setupImage();
    }
    updateImageSize() {
        const vpd = this.getViewportDimensions();
        const imgd = this.getImageDimensions();
        let scaledWidth = imgd.width;
        let scaledHeight = imgd.height;
        if (scaledWidth > vpd.width) {
            scaledHeight = scaledHeight * vpd.width / scaledWidth;
            scaledWidth = vpd.width;
        }
        if (scaledHeight > vpd.height) {
            scaledWidth = scaledWidth * vpd.height / scaledHeight;
            scaledHeight = scaledHeight;
        }
        this.imgRatio.style.width = scaledWidth + 'px';
        this.imgRatio.style.height = scaledHeight + 'px';
    }
    showNextImage(delta = 1, circular = false) {
        const newIndex = circular
            ? (this.activeImage + delta) % this.images.length
            : this.activeImage + delta;
        if (newIndex >= 0 && newIndex < this.images.length) {
            this.setupImage(newIndex);
        }
    }
    reset() {
        this.overlay.classList.remove('has-prev-images');
        this.overlay.classList.remove('has-next-images');
        this.caption.innerHTML = '';
        this.imgRatio.innerHTML = '';
    }
    setupImage(index = this.activeImage) {
        const image = this.images[index];
        if (!image)
            return;
        this.reset();
        this.activeImage = index;
        this.updateImageSize();
        this.setupNextPrevButtons(index);
        this.setupCaption(image);
        this.imgRatio.append(this.createThumnailImg(image), this.createOriginalImg(image));
    }
    createThumnailImg(image) {
        const img = this.doc.createElement('img');
        img.classList.add('gallery-img-thumbnail');
        if (image.dataset.galleryBlurredSrc) {
            img.src = image.dataset.galleryBlurredSrc;
        }
        else {
            img.src = image.src || image.dataset.src || '';
            img.srcset = image.srcset || image.dataset.srcset || '';
        }
        img.alt = image.alt;
        this.handleBrokenImage(img);
        return img;
    }
    createOriginalImg(image) {
        const img = this.doc.createElement('img');
        const src = image.dataset.originalSrc || image.src;
        const delay = image.dataset.galleryDelay || 0;
        img.classList.add('gallery-img-original');
        img.alt = image.alt;
        if (delay) {
            this.win.setTimeout(() => { img.src = src; }, parseNumber(delay));
        }
        else {
            img.src = src;
        }
        this.handleBrokenImage(img);
        this.handleLoadedImage(img);
        return img;
    }
    setupNextPrevButtons(index) {
        if (this.images.length > 1) {
            if (index > 0) {
                this.overlay.classList.add('has-prev-images');
            }
            if (index < this.images.length - 1) {
                this.overlay.classList.add('has-next-images');
            }
        }
    }
    setupCaption(image) {
        const parentFigure = ElementMatcher.findParentOrSelf(image, 'figure');
        const figureCaption = parentFigure
            ? parentFigure.querySelector('figcaption')
            : null;
        const newCaption = figureCaption
            ? figureCaption.cloneNode(true)
            : this.createAltCaption(image);
        this.caption.appendChild(newCaption);
    }
    createAltCaption(image) {
        const altCaption = this.doc.createElement('span');
        altCaption.innerHTML = image.alt;
        return altCaption;
    }
    handleBrokenImage(image) {
        image.addEventListener('error', () => image.classList.add('img-broken'));
    }
    handleLoadedImage(image) {
        const loadedClass = 'img-loaded';
        if (image.src && image.complete) {
            image.classList.add(loadedClass);
        }
        else {
            image.onload = () => image.classList.add(loadedClass);
        }
    }
    getByClass(parent, className) {
        return parent.getElementsByClassName(className)[0];
    }
    getImageDimensions() {
        const image = this.images[this.activeImage];
        return new Dimensions(parseNumber(image.dataset.originalWidth || image.naturalWidth), parseNumber(image.dataset.originalHeight || image.naturalHeight));
    }
    getViewportDimensions() {
        const w = Math.max(this.doc.documentElement.clientWidth, this.win.innerWidth || 0);
        const h = Math.max(this.doc.documentElement.clientHeight, this.win.innerHeight || 0);
        return new Dimensions(w, h);
    }
}
export class ImageGallery {
    constructor(win = window) {
        this.win = win;
        this.doc = this.win.document;
        this.scrollXBackup = 0;
        this.scrollYBackup = 0;
        this.windowListeners = {
            keyup: this.onKeyEvent.bind(this),
            resize: this.onResize.bind(this),
            swipe: this.onSwipe.bind(this)
        };
    }
    activate() {
        this.doc.addEventListener('click', (event) => {
            ElementMatcher.emittedByOrChild(event, 'img[data-gallery]', (e) => this.handleGalleryImgClick(e));
        });
    }
    getOverlay() {
        const previous = this.doc.querySelector('.gallery-overlay');
        if (previous)
            return previous;
        const overlay = ElementCreator.createFromHtml(galleryOverlayHtml);
        this.doc.body.prepend(overlay);
        this.setupListeners(overlay);
        return overlay;
    }
    setupListeners(overlay) {
        overlay.addEventListener('click', () => this.close());
        this.getByClass(overlay, 'gallery-caption')
            .addEventListener('click', e => e.stopPropagation());
        this.getByClass(overlay, 'gallery-btn-close')
            .addEventListener('click', e => {
            e.stopPropagation();
            this.close();
        });
        this.getByClass(overlay, 'gallery-btn-left')
            .addEventListener('click', e => {
            e.stopPropagation();
            this.showNextImage(-1);
        });
        this.getByClass(overlay, 'gallery-btn-right')
            .addEventListener('click', e => {
            e.stopPropagation();
            this.showNextImage(+1);
        });
    }
    registerOpenedGalleryListeners() {
        Object.entries(this.windowListeners)
            .forEach(([event, action]) => this.win.addEventListener(event, action));
    }
    onKeyEvent(event) {
        switch (event.keyCode) {
            case 27:
                this.close();
                break; // ESC
            case 37:
                this.showNextImage(-1);
                break; // Left Arrow
            case 39:
                this.showNextImage(+1);
                break; // Right Arrow
            case 32: // Space
            case 13: // Enter
                this.showNextImage(+1, true);
                break;
        }
    }
    onResize(event) {
        if (this.openedGallery) {
            this.openedGallery.updateImageSize();
        }
    }
    onSwipe(event) {
        if (!this.openedGallery)
            return;
        if (event.detail.swipeType === 'left') {
            this.openedGallery.showNextImage(+1);
        }
        else if (event.detail.swipeType === 'right') {
            this.openedGallery.showNextImage(-1);
        }
        else {
            this.close();
        }
    }
    unregisterOpenedGalleryListeners() {
        Object.entries(this.windowListeners)
            .forEach(([event, action]) => this.win.removeEventListener(event, action));
    }
    showNextImage(delta = 1, circular = false) {
        if (this.openedGallery) {
            this.openedGallery.showNextImage(delta, circular);
        }
    }
    handleGalleryImgClick(image) {
        const images = this.findAllGalleryImages(image);
        if (images.length <= 0)
            return;
        this.registerOpenedGalleryListeners();
        this.scrollXBackup = this.win.scrollX;
        this.scrollYBackup = this.win.scrollY;
        this.doc.body.classList.add('noscroll');
        const overlay = this.getOverlay();
        overlay.classList.add('show');
        const imageIndex = images.indexOf(image);
        this.openedGallery = new OpenedGallery(this.win, overlay, images, imageIndex);
    }
    findAllGalleryImages(image) {
        return image.dataset.gallery
            ? this.findAllImages(image.dataset.gallery)
            : [image];
    }
    findAllImages(galleryName) {
        const nodes = this.doc.querySelectorAll(`img[data-gallery="${galleryName}"]`);
        return Array.from(nodes);
    }
    close() {
        if (this.openedGallery) {
            this.unregisterOpenedGalleryListeners();
            const overlay = this.getOverlay();
            overlay.classList.remove('show');
            this.doc.body.classList.remove('noscroll');
            this.win.scrollTo(this.scrollXBackup, this.scrollYBackup);
            this.openedGallery.reset();
            this.openedGallery = undefined;
        }
    }
    getByClass(parent, className) {
        return parent.getElementsByClassName(className)[0];
    }
}
